/* eslint-disable dot-notation */
import { TWidget } from 'widgets/Widget';
import { postJsonData } from 'widgets/toolbox/ajax';
import { setCookie } from 'widgets/toolbox/cookie';

interface IKlaviyoJS {
    sfccEmailRegex: RegExp;
    emailFieldSelectors: Array<string>;
    identifiedUser: boolean;
    exchangeID: string;
    phone: string;
    email: string;
}

interface IKlaviyoData {
    KLCheckoutSelector: string;
    KLSelectors: Array<string> | Record<string, string>;
    KLEmailListID: string;
    startedCheckoutEventURL: string;
    subscribeURL: string;
    isLoggedInInitialization: boolean;
}

const klaviyoJS: IKlaviyoJS = {
    sfccEmailRegex: /^[\w.%+-]+@[\w.-]+\.[\w]{2,6}$/,
    emailFieldSelectors: [],
    identifiedUser: false,
    exchangeID: '',
    phone: '',
    email: ''
};

const SERVICE_CHANGE_EVENT = 'consentServiceChange';
const CONSENT_SERVICE_REQUEST_EVENT = 'consentServiceRequest';
const SERVICE_KLAVIYO = 'Klaviyo';

let subscribeUrl: string;

/**
 * Retrieves the Klaviyo exchange ID from the browser's cookies.
 *
 * @returns The Klaviyo exchange ID, or an empty string if not found.
 */
function getExchangeID(): string {
    const klaviyoCookie = document.cookie.split('; ').filter(function (c) {
        return /__kla_id=/.test(c);
    });

    if (klaviyoCookie.length > 0) {
        return JSON.parse(atob(klaviyoCookie[0].split('__kla_id=')[1])).$exchange_id;
    }

    return '';
}

/**
 * Identifies a user by their phone number using Klaviyo's `identify` method.
 *
 * @param inputVal - The phone number to identify.
 */
function identifyPhone(inputVal: string) {
    const formattedPhone = `+${inputVal.replace(/[^a-z0-9]/gi, '')}`;
    const e164PhoneRegex = /^\+[1-9]\d{1,14}$/;

    if (e164PhoneRegex.test(formattedPhone)) {
        window['klaviyo'].identify({ $phone_number: formattedPhone }).then(() => {
            klaviyoJS.identifiedUser = true;
            klaviyoJS.exchangeID = getExchangeID();
            klaviyoJS.phone = formattedPhone;
        });
    }
}

/**
 * Identifies a user by either their phone number or email address using Klaviyo's `identify` method.
 *
 * @param inputVal - The input event containing the value to identify.
 */
function phoneOrEmail(inputVal: Event) {
    const target = inputVal.target as HTMLInputElement;

    if (klaviyoJS.sfccEmailRegex.test(target.value)) {
        window['klaviyo'].identify({ $email: target.value }).then(() => {
            klaviyoJS.identifiedUser = true;
            klaviyoJS.email = target.value;
            klaviyoJS.exchangeID = getExchangeID();
        });
    }

    if (Number(target.value.replace(/[^a-z0-9]/gi, ''))) {
        identifyPhone(target.value);
    }
}

/**
 * Subscribes or unsubscribes an email address to a Klaviyo list.
 *
 * @param subscribeURL - The URL to post the subscription data to.
 * @param isSubscribed - Whether to subscribe (true) or unsubscribe (false).
 */
function subscribe(subscribeURL: string, isSubscribed) {
    const data = {
        e: isSubscribed
    };

    postJsonData(subscribeURL, data).catch(() => {});
}

/**
 * Handles the change event for Klaviyo input fields.
 *
 * @param input - The input event.
 */
function klaviyoChangeEvent(input: Event) {
    const target = input.target as HTMLInputElement;
    const isAnonymous = window.sfccUnCachedData.customerData.isAnonymous;
    let isValueEligible = false;

    if (target.value) {
        if (klaviyoJS.sfccEmailRegex.test(target.value)) {
            isValueEligible = target.value !== klaviyoJS.email;
        } else {
            isValueEligible = target.value !== klaviyoJS.phone;
        }
    }

    if ((!klaviyoJS.identifiedUser || isAnonymous) && isValueEligible) {
        setCookie('__kla_id', '', 1);
        phoneOrEmail(input);
    }
}

/**
 * Initializes event listeners for input elements to handle changes and trigger Klaviyo events.
 *
 * @param input - The HTML input element to attach listeners to.
 */

function initInputListeners(input: HTMLInputElement) {
    if (input && !input.hasAttribute('data-listener')) {
        let timeout: ReturnType<typeof setTimeout>;

        input.setAttribute('data-listener', 'klaviyo');
        input.addEventListener('change', event => {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                klaviyoChangeEvent(event);
            }, 200);
        });
    }
}

/**
 * Sets up Klaviyo event listeners on specified email input fields.
 */
function klaviyoListeners() {
    if (!klaviyoJS.emailFieldSelectors) {
        return;
    }

    klaviyoJS.emailFieldSelectors.forEach(element => {
        const selectedInputs = document.querySelectorAll(element);

        if (selectedInputs.length) {
            selectedInputs.forEach(inputElement => {
                initInputListeners(inputElement as HTMLInputElement);
            });
        }
    });
}

/**
 * Triggers a Klaviyo page tracking event based on data attributes.
 */
function pageEventTrack() {
    const pageEventTracker = document.querySelector('.js-klaviyo-page-event-tracker') as HTMLElement;

    if (!pageEventTracker) {
        return;
    }

    const action = pageEventTracker.dataset.klaviyoAction;
    const dataObj = pageEventTracker.dataset.klaviyoEventData;

    if (!action || !dataObj || !window['klaviyo']) {
        return;
    }

    window['klaviyo'].push(['track', action, JSON.parse(dataObj)]);
}

/**
 * Initializes the started checkout event listener for Klaviyo.
 *
 * @param klaviyoData - The Klaviyo data object.
 */
function initStartedCheckoutEvent(klaviyoData: IKlaviyoData) {
    if (klaviyoData.KLCheckoutSelector) {
        const selectedInputs = document.querySelectorAll(klaviyoData.KLCheckoutSelector);

        if (selectedInputs.length) {
            selectedInputs.forEach(inputElement => {
                const klavInput = inputElement as HTMLInputElement;

                klavInput.addEventListener('change', () => {
                    if (klaviyoJS.sfccEmailRegex.test(klavInput.value)) {
                        postJsonData(klaviyoData.startedCheckoutEventURL, { a: btoa(klavInput.value) }).catch(() => {});
                    }
                });
            });
        }
    }
}

/**
 * Initializes checkout events and sets up listeners for Klaviyo.
 *
 * @param klaviyoData - The Klaviyo data object.
 */
function initCheckoutEvents(klaviyoData: IKlaviyoData) {
    initStartedCheckoutEvent(klaviyoData);

    if (
        klaviyoData.KLCheckoutSelector ||
        (Array.isArray(klaviyoData.KLSelectors) && klaviyoData.KLSelectors.length) ||
        (typeof klaviyoData.KLSelectors === 'object' && Object.keys(klaviyoData.KLSelectors).length > 0)
    ) {
        const KLselectors = klaviyoData.KLSelectors;

        klaviyoJS.emailFieldSelectors = Array.isArray(KLselectors) ? KLselectors : Object.values(KLselectors);

        if (klaviyoData.KLCheckoutSelector) {
            klaviyoJS.emailFieldSelectors.push(klaviyoData.KLCheckoutSelector);
        }

        Promise.resolve(window['klaviyo'].isIdentified()).then(result => {
            klaviyoJS.identifiedUser = result || klaviyoData.isLoggedInInitialization;
        });

        if ('MutationObserver' in window) {
            const page = document.body;
            const observer = new MutationObserver(() => {
                klaviyoListeners();
            });

            observer.observe(page, {
                childList: true,
                subtree: true
            });
        }

        klaviyoListeners();
    }
}

/**
 * @param Widget Base widget for extending
 */
function KlaviyoListenersClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @class KlaviyoListeners
     * @augments Widget
     * @classdesc KlaviyoListeners Widget - used to manage Klaviyo event listeners and interactions.
     */
    class KlaviyoListeners extends Widget {
        emailSubscribeElements: Array<HTMLInputElement> = [];

        emailSubscribeInitialized: boolean = false;

        isConsentGiven: boolean = false;

        isInstanceInitialized: boolean = false;

        prefs() {
            return {
                selector: [],
                checkoutSelector: '',
                emailListId: '',
                startedCheckoutEventUrl: '',
                subscribeUrl: '',
                isLoggedInInitialization: false,
                ...super.prefs()
            };
        }

        /**
         * Initializes the KlaviyoListeners widget.
         * Sets up event listeners and handles email subscription logic.
         */
        init() {
            super.init();

            this.eventBus().on(SERVICE_CHANGE_EVENT, 'processServiceChange');
            this.eventBus().emit(CONSENT_SERVICE_REQUEST_EVENT, SERVICE_KLAVIYO);

            this.initInstanceWithDelay();
        }

        /**
         * @description Handles changes to service consent, specifically for Klaviyo services.
         * @param type - The type of service for which the consent change is being processed.
         * @param accepted - A boolean indicating whether the consent was accepted or declined.
         */
        processServiceChange(type: string, accepted: boolean) {
            if (type === SERVICE_KLAVIYO) {
                this.isConsentGiven = accepted;
                this.initInstanceWithDelay();
            }
        }

        /**
         * @description itializes the Klaviyo instance if consent has been given.
         * This method sets up checkout events, email subscription listeners, and page tracking with delay till page fully loaded.
         */
        initInstanceWithDelay() {
            if (document.readyState !== 'complete') {
                document.addEventListener('readystatechange', event => {
                    if ((event.target as Document).readyState === 'complete') {
                        setTimeout(() => this.initInstance(), 0);
                    }
                });
            } else {
                this.initInstance();
            }
        }

        /**
         * @description Initializes the Klaviyo instance if consent has been given.
         * This method sets up checkout events, email subscription listeners, and page tracking.
         */
        initInstance() {
            if (!this.isConsentGiven || this.isInstanceInitialized || !window['klaviyo']) {
                return;
            }

            const klaviyoData: IKlaviyoData = {
                KLCheckoutSelector: this.prefs().checkoutSelector,
                KLSelectors: this.prefs().selector,
                startedCheckoutEventURL: this.prefs().startedCheckoutEventUrl,
                subscribeURL: this.prefs().subscribeUrl,
                KLEmailListID: this.prefs().emailListId,
                isLoggedInInitialization: this.prefs().isLoggedInInitialization
            };

            subscribeUrl = this.prefs().subscribeUrl;

            initCheckoutEvents(klaviyoData);

            if (klaviyoData.KLEmailListID) {
                this.updateElementsEventListener();
                this.emailSubscribeInitialized = true;
                this.eventBus().on('availabilityReminderModal.showed', 'updateElementsEventListener');
            }

            pageEventTrack();
            this.isInstanceInitialized = true;
        }

        /**
         * @description Attaches event listeners to email subscription checkboxes.
         * This method finds specific subscription checkboxes on the page,
         * checks their initial state to sync with Klaviyo, and adds click
         * event listeners to handle subscription status changes.
         * @listens EventBus#drawershow
         */
        updateElementsEventListener(): void {
            const elements = [
                'dwfrm_checkoutNewPasswords_addToEmailList_dwfrm_checkoutNewPasswords_addToEmailList',
                'dwfrm_profile_customer_addToEmailList_dwfrm_profile_customer_addToEmailList',
                'dwfrm_summary_addToEmailList_dwfrm_summary_addToEmailList',
                'dwfrm_emailsubscribe_agreeToPrivacy_dwfrm_emailsubscribe_agreeToPrivacy'
            ];

            this.emailSubscribeElements.forEach(element => {
                element.removeEventListener('click', this.subscribeEmail);
            });

            setTimeout(() => {
                this.emailSubscribeElements = [];
                elements.forEach(element => {
                    const selector = `[id^="${element}"]`;
                    const emailSubscribe = document.querySelectorAll<HTMLInputElement>(selector);

                    if (emailSubscribe.length) {
                        emailSubscribe.forEach(this.handleEmailSubscribeElement.bind(this));
                    }
                });
            }, 100);
        }

        /**
         * @description Handles a single email subscription checkbox element.
         * Checks its initial state, adds a click listener, and tracks the element.
         * @param emailSubscribeElement - The subscription checkbox element.
         * @private
         */
        private handleEmailSubscribeElement(emailSubscribeElement: HTMLInputElement): void {
            if (emailSubscribeElement.checked && !this.emailSubscribeInitialized) {
                subscribe(subscribeUrl, true);
            }

            emailSubscribeElement.addEventListener('click', this.subscribeEmail);

            this.emailSubscribeElements.push(emailSubscribeElement);
        }

        /**
         * @description Event handler for email subscription checkbox clicks.
         * Determines the subscription status from the event target and
         * calls the `subscribe` function to update the user's subscription status.
         * @param event - The click event object from the checkbox.
         * @listens HTMLInputElement#click
         */
        subscribeEmail(event: Event): void {
            const isSubscribed = (event?.target as HTMLInputElement)?.checked || false;

            subscribe(subscribeUrl, isSubscribed);
        }
    }

    return KlaviyoListeners;
}

export type TKlaviyoListeners = ReturnType<typeof KlaviyoListenersClassCreator>;

export type TKlaviyoListenersInstance = InstanceType<TKlaviyoListeners>;

export default KlaviyoListenersClassCreator;
